import { Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { Button } from '../../../../../../../components/atoms/button/button';
import { ProgressBar } from '../../../../../../../components/atoms/progress-bar/progress-bar';
import { useAuth } from '../../../../../../../hooks/use-auth';
import { useAjusteAntecipacao } from '../../../../../../../hooks/use-ajuste-antecipacao';
import {
  removeAjusteAntecipacaoThunk,
  upsertAjusteAntecipacaoThunk,
} from '../../../../../../../store/slices/ajuste-antecipacao.slice';
import {
  AjusteAntecipacao,
  MoedaAjuste,
  MOEDA_AJUSTE_LABEL,
  UpsertAjusteAntecipacaoPayload,
} from '../../../../../../../types/ajuste-antecipacao';
import { AjusteAntecipacaoModal } from './ajuste-antecipacao-modal';
import { formatDateTime, formatValorMoeda } from './format';

interface Props {
  clienteId: string;
}

const Card = styled.section`
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  grid-column: 1 / -1;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
`;

const Empty = styled.p`
  margin: 6px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Rows = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Row = styled.li`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgSurface};
  font-size: 14px;
`;

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  strong {
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const RowStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-variant-numeric: tabular-nums;

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const RowFoot = styled.span`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
`;

const Skeleton = styled.div`
  height: 28px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.bgMuted} 0%,
    ${({ theme }) => theme.colors.bgSurface} 50%,
    ${({ theme }) => theme.colors.bgMuted} 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  & + & {
    margin-top: 6px;
  }
`;

const ErrorAlert = styled.div`
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.dangerBg};
  border-left: 3px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  font-size: 13px;
`;

export const AjusteAntecipacaoCard = ({ clienteId }: Props) => {
  const { hasPermission } = useAuth();
  const {
    items,
    status,
    error,
    mutating,
    mutationError,
    refresh,
    upsert,
    remove,
    clearMutationError,
  } = useAjusteAntecipacao(clienteId);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AjusteAntecipacao | null>(null);

  const canRead = hasPermission('AJUSTE_ANTECIPACAO_READ');
  const canUpdate = hasPermission('AJUSTE_ANTECIPACAO_UPDATE');

  if (!canRead) return null;

  const isLoading = status === 'loading';
  const hasError = status === 'error';
  const saving = mutating?.action === 'upsert';
  const removing = mutating?.action === 'remove';

  const openCreate = () => {
    clearMutationError();
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (item: AjusteAntecipacao) => {
    clearMutationError();
    setEditing(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    clearMutationError();
  };

  const handleSubmit = async (
    moeda: MoedaAjuste,
    payload: UpsertAjusteAntecipacaoPayload,
  ) => {
    const result = await upsert(moeda, payload);
    if (result && upsertAjusteAntecipacaoThunk.fulfilled.match(result)) {
      toast.success('Ajuste registrado');
      setModalOpen(false);
    } else {
      toast.error('Não foi possível registrar o ajuste');
    }
  };

  const handleRemove = async (moeda: MoedaAjuste) => {
    const result = await remove(moeda);
    if (result && removeAjusteAntecipacaoThunk.fulfilled.match(result)) {
      toast.success('Ajuste zerado');
      setModalOpen(false);
    } else {
      toast.error('Não foi possível zerar o ajuste');
    }
  };

  return (
    <Card data-testid="ajuste-antecipacao-card">
      <Header>
        <Title>
          <ShieldAlert size={14} />
          Controle interno de antecipação
        </Title>
        {canUpdate && !isLoading && !hasError && (
          <Button
            variant="secondary"
            size="sm"
            startIcon={<Plus size={14} />}
            onClick={openCreate}
            data-testid="ajuste-antecipacao-novo"
          >
            Registrar ajuste
          </Button>
        )}
      </Header>

      <Hint>
        Marcador operacional para casos de saldo antecipado incorretamente. Não
        impacta saldo disponível, saque, recebíveis ou reservas. Não aparece
        para o seller.
      </Hint>

      {isLoading && (
        <div data-testid="ajuste-antecipacao-loading">
          <Skeleton />
          <Skeleton />
        </div>
      )}

      {hasError && (
        <ErrorAlert>
          {error ?? 'Não foi possível carregar os ajustes.'}{' '}
          <Button variant="ghost" size="sm" onClick={() => refresh()}>
            Tentar novamente
          </Button>
        </ErrorAlert>
      )}

      {!isLoading && !hasError && items.length === 0 && (
        <Empty>Nenhum ajuste registrado para este cliente.</Empty>
      )}

      {!isLoading && !hasError && items.length > 0 && (
        <Rows>
          {items.map((item) => {
            const baselineAusente = item.valorInicial <= 0;
            const quitado = item.valor === 0 && item.valorInicial > 0;
            const variant = quitado ? 'success' : 'brand';
            const labelBarra = baselineAusente
              ? 'Sem baseline'
              : `${item.percentualQuitado.toFixed(0)}%`;
            return (
              <Row key={item.moeda} data-testid={`ajuste-row-${item.moeda}`}>
                <RowHeader>
                  <strong>
                    {MOEDA_AJUSTE_LABEL[item.moeda] ?? item.moedaSigla}
                  </strong>
                  {canUpdate && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(item)}
                      data-testid={`ajuste-antecipacao-editar-${item.moeda}`}
                    >
                      Editar
                    </Button>
                  )}
                </RowHeader>
                <ProgressBar
                  value={item.percentualQuitado}
                  label={labelBarra}
                  variant={variant}
                  ariaLabel={`Progresso de quitação ${MOEDA_AJUSTE_LABEL[item.moeda] ?? item.moedaSigla}`}
                  testId={`ajuste-progress-${item.moeda}`}
                />
                {baselineAusente ? (
                  <RowStats data-testid={`ajuste-stats-${item.moeda}`}>
                    <span>
                      Pendente{' '}
                      <strong>
                        {formatValorMoeda(item.valor, item.moedaSigla)}
                      </strong>
                    </span>
                    <RowFoot>Sem baseline registrado.</RowFoot>
                  </RowStats>
                ) : (
                  <RowStats data-testid={`ajuste-stats-${item.moeda}`}>
                    <span>
                      Quitado{' '}
                      <strong>
                        {formatValorMoeda(item.valorPago, item.moedaSigla)}
                      </strong>
                    </span>
                    <span>·</span>
                    <span>
                      Pendente{' '}
                      <strong>
                        {formatValorMoeda(item.valor, item.moedaSigla)}
                      </strong>
                    </span>
                    <span>·</span>
                    <span>
                      Total{' '}
                      <strong>
                        {formatValorMoeda(item.valorInicial, item.moedaSigla)}
                      </strong>
                    </span>
                  </RowStats>
                )}
                <RowFoot>
                  Atualizado em {formatDateTime(item.updatedAt)}
                </RowFoot>
              </Row>
            );
          })}
        </Rows>
      )}

      {modalOpen && (
        <AjusteAntecipacaoModal
          isOpen={modalOpen}
          existente={editing}
          saving={saving}
          removing={removing}
          errorMessage={mutationError}
          onSubmit={handleSubmit}
          onRemove={editing ? handleRemove : undefined}
          onClose={closeModal}
        />
      )}
    </Card>
  );
};
